import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { t } from 'i18next'
import DynamicTable from 'Components/DynamicTable/DynamicTable'
import CustomSnackbar from 'Components/CustomSnackbar'
import MiniLoading from 'Components/MiniLoading'
import useColumnPickingFlow, {
  PickingFlowSelectionProvider,
} from './hooks/useColumnPickingFlow'
import AddBranchModal from './components/AddBranchModal/AddBranchModal'
import DeleteBranchModal from './components/DeleteBranchModal/DeleteBranchModal'
import {
  fetchPickingFlowBranches,
  savePickingFlowBranches,
} from 'redux/actions/pickingFlowActions'
import searchIcon from 'Assets/Images/BuscarCotizacion/search.svg'
import './pickingFlowShared.css'
import './PickingFlowManager.css'

const getActiveIdSet = branches =>
  new Set(
    branches
      .filter(branch => branch.Active)
      .map(branch => branch.BranchOfficeId)
  )

const sortBranchesByCompany = branches =>
  [...branches].sort((firstBranch, secondBranch) =>
    (firstBranch.CompanyName || '').localeCompare(
      secondBranch.CompanyName || '',
      'es',
      { sensitivity: 'base' }
    )
  )

const PickingFlowManager = () => {
  const dispatch = useDispatch()

  const [catalog, setCatalog] = useState([])
  const [workingBranches, setWorkingBranches] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loadingData, setLoadingData] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [selectedBranchIds, setSelectedBranchIds] = useState(new Set())
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const loadBranches = async () => {
    setLoadingData(true)
    try {
      const branches = await dispatch(fetchPickingFlowBranches())
      const activeIds = getActiveIdSet(branches)

      setCatalog(branches)
      setWorkingBranches(
        sortBranchesByCompany(
          branches.filter(branch => activeIds.has(branch.BranchOfficeId))
        )
      )
      setSelectedBranchIds(new Set())
    } catch (error) {
      console.error('Error loading picking flow branches:', error)
      setSnackbarMessage({
        open: true,
        message: t('PICKING_FLOW_SETTINGS.MESSAGE.ERROR_LOAD'),
        severity: 'error',
      })
    } finally {
      setLoadingData(false)
    }
  }

  useEffect(() => {
    loadBranches().then()
  }, [])

  const savedActiveIds = useMemo(() => getActiveIdSet(catalog), [catalog])

  const hasPendingChanges = useMemo(
    () =>
      workingBranches.length !== savedActiveIds.size ||
      workingBranches.some(
        branch => !savedActiveIds.has(branch.BranchOfficeId)
      ),
    [workingBranches, savedActiveIds]
  )

  const filteredWorkingBranches = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()
    if (!normalizedSearch) return workingBranches

    return workingBranches.filter(branch =>
      (branch.Name || '').toLowerCase().includes(normalizedSearch)
    )
  }, [workingBranches, searchText])

  const visibleBranchIds = useMemo(
    () => filteredWorkingBranches.map(branch => branch.BranchOfficeId),
    [filteredWorkingBranches]
  )

  const availableBranches = useMemo(() => {
    const workingIds = new Set(
      workingBranches.map(branch => branch.BranchOfficeId)
    )
    return catalog.filter(branch => !workingIds.has(branch.BranchOfficeId))
  }, [catalog, workingBranches])

  const handleCloseSnackbar = () => {
    setSnackbarMessage(prev => ({ ...prev, open: false }))
  }

  const handleToggleSelect = useCallback(branchId => {
    setSelectedBranchIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(branchId)) {
        newSet.delete(branchId)
      } else {
        newSet.add(branchId)
      }
      return newSet
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    setSelectedBranchIds(prev => {
      const allIds = filteredWorkingBranches.map(
        branch => branch.BranchOfficeId
      )
      const areAllVisibleSelected =
        allIds.length > 0 && allIds.every(branchId => prev.has(branchId))
      const nextSelectedIds = new Set(prev)

      if (areAllVisibleSelected) {
        allIds.forEach(branchId => nextSelectedIds.delete(branchId))
      } else {
        allIds.forEach(branchId => nextSelectedIds.add(branchId))
      }

      return nextSelectedIds
    })
  }, [filteredWorkingBranches])

  const handleOpenDeleteModal = useCallback(branch => {
    setDeleteTarget(branch)
  }, [])

  const handleOpenDeleteSelectedModal = useCallback(() => {
    const selectedBranches = workingBranches.filter(branch =>
      selectedBranchIds.has(branch.BranchOfficeId)
    )
    setDeleteTarget(selectedBranches)
  }, [workingBranches, selectedBranchIds])

  const handleConfirmDelete = () => {
    if (Array.isArray(deleteTarget)) {
      const idsToDelete = new Set(
        deleteTarget.map(branch => branch.BranchOfficeId)
      )
      setWorkingBranches(prev =>
        prev.filter(branch => !idsToDelete.has(branch.BranchOfficeId))
      )
      setSelectedBranchIds(new Set())
    } else {
      setWorkingBranches(prev =>
        prev.filter(
          branch => branch.BranchOfficeId !== deleteTarget.BranchOfficeId
        )
      )
      setSelectedBranchIds(prev => {
        const nextSelectedIds = new Set(prev)
        nextSelectedIds.delete(deleteTarget.BranchOfficeId)
        return nextSelectedIds
      })
    }
    setDeleteTarget(null)
  }

  const handleAddBranches = selectedBranches => {
    setWorkingBranches(prev =>
      sortBranchesByCompany([...prev, ...selectedBranches])
    )
    setIsAddModalOpen(false)
  }

  const handleSave = async () => {
    setIsSaving(true)
    const activeIds = workingBranches.map(branch => branch.BranchOfficeId)
    const previouslyActiveBranches = catalog.filter(branch => branch.Active)
    const success = await dispatch(
      savePickingFlowBranches(workingBranches, previouslyActiveBranches)
    )
    setIsSaving(false)

    if (success) {
      setCatalog(prev =>
        prev.map(branch => ({
          ...branch,
          Active: activeIds.includes(branch.BranchOfficeId),
        }))
      )
      setSelectedBranchIds(new Set())
      setSnackbarMessage({
        open: true,
        message: t('PICKING_FLOW_SETTINGS.MESSAGE.SUCCESS_SAVE'),
        severity: 'success',
      })
    } else {
      await loadBranches()
      setSnackbarMessage({
        open: true,
        message: t('PICKING_FLOW_SETTINGS.MESSAGE.ERROR_SAVE'),
        severity: 'error',
      })
    }
  }

  const handleCancel = () => {
    setWorkingBranches(
      sortBranchesByCompany(
        catalog.filter(branch => savedActiveIds.has(branch.BranchOfficeId))
      )
    )
    setSelectedBranchIds(new Set())
  }

  const columns = useColumnPickingFlow({ onDelete: handleOpenDeleteModal })

  return (
    <>
      <div
        className="picking-flow-manager"
        data-testid="container-picking-flow-manager">
        <div
          className="picking-flow-toolbar"
          data-testid="toolbar-picking-flow">
          <div
            className="picking-flow-search-group"
            data-testid="search-group-picking-flow">
            <input
              type="text"
              data-testid="input-picking-flow-search"
              placeholder={t('PICKING_FLOW_SETTINGS.LIST.SEARCH_PLACEHOLDER')}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
            />
            <img
              src={searchIcon}
              className="picking-flow-search-icon"
              alt="search"
            />
          </div>
          <div
            className="picking-flow-toolbar-actions"
            data-testid="toolbar-actions-picking-flow">
            {selectedBranchIds.size > 0 && (
              <button
                type="button"
                className="picking-flow-btn-delete"
                data-testid="btn-delete-selected-picking-flow"
                onClick={handleOpenDeleteSelectedModal}>
                {t('PICKING_FLOW_SETTINGS.LIST.DELETE_SELECTED', {
                  count: selectedBranchIds.size,
                })}
              </button>
            )}
            <button
              type="button"
              className="picking-flow-btn-solid"
              data-testid="btn-open-add-picking-flow-branch"
              onClick={() => setIsAddModalOpen(true)}>
              {t('PICKING_FLOW_SETTINGS.LIST.ADD_BRANCH')}
            </button>
          </div>
        </div>

        {loadingData ? (
          <div data-testid="loading-picking-flow-branches">
            <MiniLoading />
          </div>
        ) : (
          <div data-testid="table-picking-flow-branches">
            <PickingFlowSelectionProvider
              selectedBranchIds={selectedBranchIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              allBranchIds={visibleBranchIds}>
              <DynamicTable
                columns={columns}
                data={filteredWorkingBranches}
                loadingData={loadingData}
                getRowId={row => row.BranchOfficeId}
              />
            </PickingFlowSelectionProvider>
          </div>
        )}

        <div className="picking-flow-footer" data-testid="footer-picking-flow">
          <p
            className="picking-flow-total"
            data-testid="picking-flow-total-branches"
            aria-live="polite">
            {t('PICKING_FLOW_SETTINGS.LIST.TOTAL_BRANCHES', {
              count: workingBranches.length,
            })}
          </p>
          <div
            className="picking-flow-footer-actions"
            data-testid="footer-actions-picking-flow">
            <button
              type="button"
              className="picking-flow-btn-outline"
              data-testid="btn-cancel-picking-flow"
              disabled={isSaving || !hasPendingChanges}
              onClick={handleCancel}>
              {t('PICKING_FLOW_SETTINGS.LIST.CANCEL')}
            </button>
            <button
              type="button"
              className="picking-flow-btn-solid"
              data-testid="btn-save-picking-flow"
              disabled={isSaving || !hasPendingChanges}
              onClick={handleSave}>
              {t('PICKING_FLOW_SETTINGS.LIST.SAVE')}
            </button>
          </div>
        </div>
      </div>

      <AddBranchModal
        open={isAddModalOpen}
        availableBranches={availableBranches}
        onAdd={handleAddBranches}
        onClose={() => setIsAddModalOpen(false)}
      />

      <DeleteBranchModal
        open={!!deleteTarget}
        count={Array.isArray(deleteTarget) ? deleteTarget.length : 1}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleCloseSnackbar}
      />
    </>
  )
}

export default PickingFlowManager
